import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import {
  CurrencyDollarIcon,
  ShoppingBagIcon,
  CubeIcon,
  UsersIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  TruckIcon,
  BuildingOfficeIcon,
  CreditCardIcon,
  BanknotesIcon,
  WalletIcon,
  DevicePhoneMobileIcon,
  XMarkIcon,
  ChevronRightIcon,
  ReceiptRefundIcon,
  BellAlertIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CalendarIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  FireIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline'
import { dashboardService } from '../../services/dashboardService'
import { notificationService } from '../../services/notificationService'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

// Parse "KSh 12,345.67" → 12345.67
const parseKsh = (val: string | undefined): number => {
  if (!val) return 0
  const num = parseFloat(val.replace(/[^0-9.]/g, ''))
  return isNaN(num) ? 0 : num
}

const StatCard = ({
  title,
  value,
  change,
  changeType,
  icon: Icon,
  href,
  onClick,
  subtitle,
  detail,
  color = 'primary',
}: {
  title: string
  value: string
  change?: string
  changeType?: 'positive' | 'negative' | 'neutral'
  icon: React.ElementType
  href?: string
  onClick?: () => void
  subtitle?: string
  detail?: { label: string; value: string; percent?: number }
  color?: 'primary' | 'blue' | 'green' | 'amber' | 'purple' | 'rose'
}) => {
  const changeColor =
    changeType === 'positive'
      ? 'text-green-600'
      : changeType === 'negative'
      ? 'text-red-600'
      : 'text-gray-500'

  const changeIcon =
    changeType === 'positive' ? (
      <ArrowTrendingUpIcon className="w-4 h-4" />
    ) : changeType === 'negative' ? (
      <ArrowTrendingDownIcon className="w-4 h-4" />
    ) : null

  const colorClasses = {
    primary: 'bg-primary-50 text-primary-600',
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    purple: 'bg-purple-50 text-purple-600',
    rose: 'bg-rose-50 text-rose-600',
  }

  const content = (
    <>
      <div className="flex items-center">
        <div className={`flex-shrink-0 p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="ml-4 flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-500 truncate">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          )}
          {detail && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs text-gray-500">{detail.label}:</span>
              <span className="text-sm font-semibold text-green-600">{detail.value}</span>
              {typeof detail.percent === 'number' && (
                <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${detail.percent >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {detail.percent >= 0 ? '+' : ''}{detail.percent.toFixed(1)}%
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {change && (
        <div className={`mt-4 flex items-center text-sm ${changeColor}`}>
          {changeIcon}
          <span className="ml-1">{change}</span>
          <span className="ml-2 text-gray-400">vs last month</span>
        </div>
      )}
    </>
  )

  const className =
    'bg-white rounded-xl shadow border border-gray-100 p-6 transition-all hover:shadow-lg cursor-pointer'

  if (href) {
    return (
      <Link to={href} className={className} onClick={onClick}>
        {content}
      </Link>
    )
  }

  return (
    <div className={className} onClick={onClick}>
      {content}
    </div>
  )
}

const AlertCard = ({
  title,
  count,
  type,
  href,
}: {
  title: string
  count: number
  type: 'warning' | 'danger' | 'info'
  href: string
}) => {
  const colors = {
    warning: 'bg-yellow-50 text-yellow-800 border-yellow-200',
    danger: 'bg-red-50 text-red-800 border-red-200',
    info: 'bg-blue-50 text-blue-800 border-blue-200',
  }

  return (
    <Link
      to={href}
      className={`flex items-center justify-between p-4 rounded-lg border ${colors[type]} hover:opacity-80 transition-opacity`}
    >
      <span className="font-medium">{title}</span>
      <span className="flex items-center justify-center w-8 h-8 bg-white rounded-full font-bold shadow-sm">
        {count}
      </span>
    </Link>
  )
}

// Modal Component for Customer Category Details
const CustomerCategoryModal = ({
  category,
  isOpen,
  onClose,
}: {
  category: any
  isOpen: boolean
  onClose: () => void
}) => {
  if (!isOpen || !category) return null

  const totalInvoices = category.customers?.reduce(
    (sum: number, c: any) => sum + (c.invoices?.length || 0),
    0
  ) || 0

  const totalOverdue = category.customers?.reduce((sum: number, c: any) => {
    const overdueInvoices = c.invoices?.filter((inv: any) => inv.isOverdue) || []
    return sum + overdueInvoices.reduce((s: number, inv: any) => s + inv.balanceDue, 0)
  }, 0) || 0

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-primary-600 px-4 py-5 sm:px-6 flex justify-between items-center">
            <div>
              <h3 className="text-lg leading-6 font-medium text-white">{category.name}</h3>
              <p className="mt-1 text-sm text-primary-100">
                {category.code} • {category.priceTier} pricing • {category.paymentTermsDays} days payment terms
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-primary-100 hover:text-white transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 bg-gray-50">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">Total Balance</p>
              <p className="text-xl font-bold text-gray-900">
                KSh {category.totalBalance?.toLocaleString()}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">Customers</p>
              <p className="text-xl font-bold text-gray-900">{category.customerCount}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">Open Invoices</p>
              <p className="text-xl font-bold text-gray-900">{totalInvoices}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">Overdue Amount</p>
              <p className="text-xl font-bold text-red-600">
                KSh {totalOverdue?.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Customers List */}
          <div className="px-6 py-4">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Customers</h4>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {category.customers?.map((customer: any) => (
                <div
                  key={customer.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <Link
                        to={`/customers/${customer.id}`}
                        className="text-primary-600 font-medium hover:underline"
                      >
                        {customer.name}
                      </Link>
                      <div className="text-sm text-gray-500 mt-1">
                        {customer.email && <span className="mr-3">{customer.email}</span>}
                        {customer.phone && <span>{customer.phone}</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">
                        KSh {customer.balance?.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">
                        Credit: KSh {customer.creditLimit?.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Invoices */}
                  {customer.invoices?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-xs font-medium text-gray-500 mb-2">Open Invoices:</p>
                      <div className="space-y-1">
                        {customer.invoices.map((inv: any) => (
                          <div
                            key={inv.id}
                            className="flex justify-between text-sm"
                          >
                            <span className={inv.isOverdue ? 'text-red-600' : 'text-gray-600'}>
                              {inv.number}
                              {inv.isOverdue && (
                                <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                                  Overdue
                                </span>
                              )}
                            </span>
                            <div className="text-right">
                              <span className="font-medium">
                                KSh {inv.balanceDue?.toLocaleString()}
                              </span>
                              <span className="text-xs text-gray-400 ml-2">
                                due {new Date(inv.dueDate).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent Payments */}
                  {customer.recentPayments?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-xs font-medium text-gray-500 mb-2">Recent Payments:</p>
                      <div className="flex flex-wrap gap-2">
                        {customer.recentPayments.map((payment: any) => (
                          <span
                            key={payment.id}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-50 text-green-700"
                          >
                            KSh {payment.amount?.toLocaleString()} • {payment.method}
                            {payment.reference && ` (${payment.reference})`}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Modal Component for Payment Mode Details
const PaymentModeModal = ({
  mode,
  isOpen,
  onClose,
}: {
  mode: any
  isOpen: boolean
  onClose: () => void
}) => {
  if (!isOpen || !mode) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          {/* Header */}
          <div className="bg-green-600 px-4 py-5 sm:px-6 flex justify-between items-center">
            <div>
              <h3 className="text-lg leading-6 font-medium text-white">{mode.name}</h3>
              <p className="mt-1 text-sm text-green-100">
                {mode.totalCount} total transactions
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-green-100 hover:text-white transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 bg-gray-50">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">Today</p>
              <p className="text-xl font-bold text-gray-900">
                KSh {mode.todayAmount?.toLocaleString()}
              </p>
              <p className="text-xs text-gray-400">{mode.todayCount} transactions</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">This Week</p>
              <p className="text-xl font-bold text-gray-900">
                KSh {mode.weekAmount?.toLocaleString()}
              </p>
              <p className="text-xs text-gray-400">{mode.weekCount} transactions</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">This Month</p>
              <p className="text-xl font-bold text-gray-900">
                KSh {mode.monthAmount?.toLocaleString()}
              </p>
              <p className="text-xs text-gray-400">{mode.monthCount} transactions</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">All Time</p>
              <p className="text-xl font-bold text-gray-900">
                KSh {mode.totalAmount?.toLocaleString()}
              </p>
              <p className="text-xs text-gray-400">{mode.totalCount} transactions</p>
            </div>
          </div>

          {/* Recent Payments */}
          <div className="px-6 py-4">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Recent Transactions</h4>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {mode.recentPayments?.map((payment: any) => (
                <div
                  key={payment.id}
                  className="flex justify-between items-center p-3 border border-gray-100 rounded-lg hover:bg-gray-50"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      KSh {payment.amount?.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500">
                      {payment.customer || payment.supplier || 'Unknown'}
                    </p>
                    {payment.bankName && (
                      <p className="text-xs text-gray-400">{payment.bankName}</p>
                    )}
                    {payment.notes && (
                      <p className="text-xs text-gray-400">{payment.notes}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">
                      {new Date(payment.date).toLocaleDateString('en-KE', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                    {payment.reference && (
                      <p className="text-xs text-gray-400">Ref: {payment.reference}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Payment Alerts Widget Component
const PaymentAlertsWidget = () => {
  const [selectedTab, setSelectedTab] = useState<'upcoming' | 'overdue' | 'terms'>('upcoming')
  const [expandedAlert, setExpandedAlert] = useState<number | null>(null)

  const { data: alertsData, isLoading } = useQuery({
    queryKey: ['dashboard', 'payment-alerts'],
    queryFn: () => dashboardService.getPaymentAlerts(),
  })

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      </div>
    )
  }

  const alerts = alertsData?.data?.data
  if (!alerts) return null

  const { summary, dueToday, dueThisWeek, dueIn30Days, overdue, byPaymentTerms } = alerts

  const hasAnyAlerts = 
    dueToday.length > 0 || 
    dueThisWeek.length > 0 || 
    dueIn30Days.length > 0 ||
    overdue['1-30'].length > 0 ||
    overdue['31-60'].length > 0 ||
    overdue['61-90'].length > 0 ||
    overdue['90+'].length > 0

  if (!hasAnyAlerts) {
    return (
      <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <CheckCircleIcon className="w-6 h-6 text-green-500" />
          <h2 className="text-lg font-semibold text-gray-900">Payment Alerts</h2>
        </div>
        <p className="text-gray-500">No upcoming or overdue payments. All caught up!</p>
      </div>
    )
  }

  const AlertCard = ({ 
    title, 
    count, 
    amount, 
    icon: Icon, 
    color, 
    items,
    isExpanded,
    onToggle,
  }: { 
    title: string
    count: number
    amount: number
    icon: React.ElementType
    color: string
    items: any[]
    isExpanded: boolean
    onToggle: () => void
  }) => (
    <div className={`rounded-lg border ${color} overflow-hidden`}>
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center justify-between hover:bg-opacity-80 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg bg-white bg-opacity-50`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="text-left">
            <p className="font-semibold">{title}</p>
            <p className="text-sm opacity-75">{count} invoice{count !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xl font-bold">KSh {amount.toLocaleString()}</p>
          <ChevronRightIcon 
            className={`w-5 h-5 ml-auto transform transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
          />
        </div>
      </button>
      
      {isExpanded && items.length > 0 && (
        <div className="border-t border-gray-200 bg-white bg-opacity-50">
          <div className="max-h-64 overflow-y-auto">
            {items.map((item: any) => (
              <Link
                key={item.invoiceId}
                to={`/invoices/${item.invoiceId}`}
                className="block p-3 hover:bg-white hover:bg-opacity-50 border-b border-gray-100 last:border-0"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-900">{item.customerName}</p>
                    <p className="text-sm text-gray-500">
                      {item.invoiceNumber} • {item.daysUntilDue > 0 ? `Due in ${item.daysUntilDue} days` : item.daysOverdue > 0 ? `${item.daysOverdue} days overdue` : 'Due today'}
                    </p>
                    {item.paymentTermsDays > 0 && (
                      <p className="text-xs text-gray-400">
                        Net {item.paymentTermsDays} payment terms
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">
                      KSh {item.amount.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">
                      Due {new Date(item.dueDate).toLocaleDateString('en-KE')}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )

  return (
    <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
      {/* Header with Summary */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <BellAlertIcon className="w-6 h-6 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Payment Alerts</h2>
          {summary.totalOverdue > 0 && (
            <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
              {summary.invoiceCount} outstanding
            </span>
          )}
        </div>
        
        {/* Summary Stats */}
        <div className="flex flex-wrap gap-4">
          {summary.totalDueToday > 0 && (
            <div className="px-3 py-2 bg-orange-50 rounded-lg">
              <p className="text-xs text-orange-600">Due Today</p>
              <p className="font-semibold text-orange-700">KSh {summary.totalDueToday.toLocaleString()}</p>
            </div>
          )}
          {summary.totalDueThisWeek > 0 && (
            <div className="px-3 py-2 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-600">This Week</p>
              <p className="font-semibold text-blue-700">KSh {summary.totalDueThisWeek.toLocaleString()}</p>
            </div>
          )}
          {summary.totalOverdue > 0 && (
            <div className="px-3 py-2 bg-red-50 rounded-lg">
              <p className="text-xs text-red-600">Overdue</p>
              <p className="font-semibold text-red-700">KSh {summary.totalOverdue.toLocaleString()}</p>
            </div>
          )}
          <div className="px-3 py-2 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600">Total Due</p>
            <p className="font-semibold text-gray-700">KSh {summary.totalDue.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        <button
          onClick={() => setSelectedTab('upcoming')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            selectedTab === 'upcoming'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Upcoming ({dueToday.length + dueThisWeek.length + dueIn30Days.length})
        </button>
        <button
          onClick={() => setSelectedTab('overdue')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            selectedTab === 'overdue'
              ? 'border-red-600 text-red-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Overdue ({overdue['1-30'].length + overdue['31-60'].length + overdue['61-90'].length + overdue['90+'].length})
        </button>
        <button
          onClick={() => setSelectedTab('terms')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            selectedTab === 'terms'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          By Terms
        </button>
      </div>

      {/* Alert Cards */}
      <div className="space-y-3">
        {selectedTab === 'upcoming' && (
          <>
            {dueToday.length > 0 && (
              <AlertCard
                title="Due Today"
                count={dueToday.length}
                amount={summary.totalDueToday}
                icon={ClockIcon}
                color="bg-orange-50 border-orange-200 text-orange-800"
                items={dueToday}
                isExpanded={expandedAlert === 1}
                onToggle={() => setExpandedAlert(expandedAlert === 1 ? null : 1)}
              />
            )}
            {dueThisWeek.length > 0 && (
              <AlertCard
                title="Due This Week"
                count={dueThisWeek.length}
                amount={summary.totalDueThisWeek - summary.totalDueToday}
                icon={CalendarIcon}
                color="bg-blue-50 border-blue-200 text-blue-800"
                items={dueThisWeek}
                isExpanded={expandedAlert === 2}
                onToggle={() => setExpandedAlert(expandedAlert === 2 ? null : 2)}
              />
            )}
            {dueIn30Days.length > 0 && (
              <AlertCard
                title="Due in 30 Days"
                count={dueIn30Days.length}
                amount={summary.totalDueIn30Days - summary.totalDueThisWeek}
                icon={CalendarIcon}
                color="bg-purple-50 border-purple-200 text-purple-800"
                items={dueIn30Days}
                isExpanded={expandedAlert === 3}
                onToggle={() => setExpandedAlert(expandedAlert === 3 ? null : 3)}
              />
            )}
          </>
        )}

        {selectedTab === 'overdue' && (
          <>
            {overdue['1-30'].length > 0 && (
              <AlertCard
                title="Overdue 1-30 Days"
                count={overdue['1-30'].length}
                amount={overdue['1-30'].reduce((sum: number, item: any) => sum + item.amount, 0)}
                icon={ExclamationCircleIcon}
                color="bg-yellow-50 border-yellow-200 text-yellow-800"
                items={overdue['1-30']}
                isExpanded={expandedAlert === 4}
                onToggle={() => setExpandedAlert(expandedAlert === 4 ? null : 4)}
              />
            )}
            {overdue['31-60'].length > 0 && (
              <AlertCard
                title="Overdue 31-60 Days"
                count={overdue['31-60'].length}
                amount={overdue['31-60'].reduce((sum: number, item: any) => sum + item.amount, 0)}
                icon={ExclamationTriangleIcon}
                color="bg-orange-50 border-orange-200 text-orange-800"
                items={overdue['31-60']}
                isExpanded={expandedAlert === 5}
                onToggle={() => setExpandedAlert(expandedAlert === 5 ? null : 5)}
              />
            )}
            {overdue['61-90'].length > 0 && (
              <AlertCard
                title="Overdue 61-90 Days"
                count={overdue['61-90'].length}
                amount={overdue['61-90'].reduce((sum: number, item: any) => sum + item.amount, 0)}
                icon={FireIcon}
                color="bg-red-50 border-red-200 text-red-800"
                items={overdue['61-90']}
                isExpanded={expandedAlert === 6}
                onToggle={() => setExpandedAlert(expandedAlert === 6 ? null : 6)}
              />
            )}
            {overdue['90+'].length > 0 && (
              <AlertCard
                title="Overdue 90+ Days"
                count={overdue['90+'].length}
                amount={overdue['90+'].reduce((sum: number, item: any) => sum + item.amount, 0)}
                icon={FireIcon}
                color="bg-rose-50 border-rose-200 text-rose-800"
                items={overdue['90+']}
                isExpanded={expandedAlert === 7}
                onToggle={() => setExpandedAlert(expandedAlert === 7 ? null : 7)}
              />
            )}
          </>
        )}

        {selectedTab === 'terms' && (
          <>
            {byPaymentTerms.immediate.length > 0 && (
              <AlertCard
                title="Immediate Payment"
                count={byPaymentTerms.immediate.length}
                amount={byPaymentTerms.immediate.reduce((sum: number, item: any) => sum + item.amount, 0)}
                icon={ClockIcon}
                color="bg-red-50 border-red-200 text-red-800"
                items={byPaymentTerms.immediate}
                isExpanded={expandedAlert === 8}
                onToggle={() => setExpandedAlert(expandedAlert === 8 ? null : 8)}
              />
            )}
            {byPaymentTerms.net15.length > 0 && (
              <AlertCard
                title="Net 15 Days"
                count={byPaymentTerms.net15.length}
                amount={byPaymentTerms.net15.reduce((sum: number, item: any) => sum + item.amount, 0)}
                icon={CalendarIcon}
                color="bg-yellow-50 border-yellow-200 text-yellow-800"
                items={byPaymentTerms.net15}
                isExpanded={expandedAlert === 9}
                onToggle={() => setExpandedAlert(expandedAlert === 9 ? null : 9)}
              />
            )}
            {byPaymentTerms.net30.length > 0 && (
              <AlertCard
                title="Net 30 Days"
                count={byPaymentTerms.net30.length}
                amount={byPaymentTerms.net30.reduce((sum: number, item: any) => sum + item.amount, 0)}
                icon={CalendarIcon}
                color="bg-blue-50 border-blue-200 text-blue-800"
                items={byPaymentTerms.net30}
                isExpanded={expandedAlert === 10}
                onToggle={() => setExpandedAlert(expandedAlert === 10 ? null : 10)}
              />
            )}
            {byPaymentTerms.net60.length > 0 && (
              <AlertCard
                title="Net 60 Days"
                count={byPaymentTerms.net60.length}
                amount={byPaymentTerms.net60.reduce((sum: number, item: any) => sum + item.amount, 0)}
                icon={CalendarIcon}
                color="bg-purple-50 border-purple-200 text-purple-800"
                items={byPaymentTerms.net60}
                isExpanded={expandedAlert === 11}
                onToggle={() => setExpandedAlert(expandedAlert === 11 ? null : 11)}
              />
            )}
            {byPaymentTerms.net90.length > 0 && (
              <AlertCard
                title="Net 90 Days"
                count={byPaymentTerms.net90.length}
                amount={byPaymentTerms.net90.reduce((sum: number, item: any) => sum + item.amount, 0)}
                icon={CalendarIcon}
                color="bg-indigo-50 border-indigo-200 text-indigo-800"
                items={byPaymentTerms.net90}
                isExpanded={expandedAlert === 12}
                onToggle={() => setExpandedAlert(expandedAlert === 12 ? null : 12)}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

// Get icon for payment method
const getPaymentIcon = (code: string) => {
  const iconMap: { [key: string]: React.ElementType } = {
    cash: BanknotesIcon,
    mpesa: DevicePhoneMobileIcon,
    bank: BuildingOfficeIcon,
    card: CreditCardIcon,
    cheque: ReceiptRefundIcon,
  }
  return iconMap[code.toLowerCase()] || WalletIcon
}

// Get color for customer category based on type
const getCategoryColor = (code: string): 'primary' | 'blue' | 'green' | 'amber' | 'purple' | 'rose' => {
  const colorMap: { [key: string]: 'primary' | 'blue' | 'green' | 'amber' | 'purple' | 'rose' } = {
    consignment: 'purple',
    wholesale: 'blue',
    retail: 'green',
    corporate: 'amber',
    walkin: 'primary',
  }
  return colorMap[code.toLowerCase()] || 'primary'
}

// Calendar Widget Component
const PaymentCalendarWidget = () => {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  
  const year = currentDate.getFullYear()
  const month = currentDate.getMonth() + 1
  
  const { data: calendarData, isLoading } = useQuery({
    queryKey: ['dashboard', 'payment-calendar', year, month],
    queryFn: () => dashboardService.getPaymentCalendar(year, month),
  })

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      </div>
    )
  }

  const calendar = calendarData?.data?.data?.calendar || []
  const stats = calendarData?.data?.data?.stats || { totalExpected: 0, totalInvoices: 0, byCategory: {} }
  
  // Create a map of dates to amounts
  const dateMap = calendar.reduce((acc: any, day: any) => {
    acc[day.date] = day
    return acc
  }, {})

  const daysInMonth = new Date(year, month, 0).getDate()

  // Only show days that have payment data, sorted by date
  const dataDays = calendar
    .map((day: any) => ({
      date: day.date,
      day: parseInt(day.date.split('-')[2]),
      data: day,
    }))
    .sort((a: any, b: any) => a.date.localeCompare(b.date))

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 2, 1))
    setSelectedDate(null)
  }

  const nextMonth = () => {
    setCurrentDate(new Date(year, month, 1))
    setSelectedDate(null)
  }

  const goToNextDate = () => {
    if (!selectedDate) {
      if (dataDays.length > 0) setSelectedDate(dataDays[0].date)
      return
    }
    const currentIndex = dataDays.findIndex((d: any) => d.date === selectedDate)
    if (currentIndex >= 0 && currentIndex < dataDays.length - 1) {
      setSelectedDate(dataDays[currentIndex + 1].date)
    }
  }

  const goToPrevDate = () => {
    if (!selectedDate) {
      if (dataDays.length > 0) setSelectedDate(dataDays[dataDays.length - 1].date)
      return
    }
    const currentIndex = dataDays.findIndex((d: any) => d.date === selectedDate)
    if (currentIndex > 0) {
      setSelectedDate(dataDays[currentIndex - 1].date)
    }
  }

  const selectedDayData = selectedDate ? dateMap[selectedDate] : null

  const getCategoryColorClass = (category: string) => {
    const colors: { [key: string]: string } = {
      corporate: 'bg-amber-100 text-amber-800 border-amber-200',
      consignment: 'bg-purple-100 text-purple-800 border-purple-200',
      wholesale: 'bg-blue-100 text-blue-800 border-blue-200',
      retail: 'bg-green-100 text-green-800 border-green-200',
      default: 'bg-gray-100 text-gray-800 border-gray-200',
    }
    return colors[category] || colors.default
  }

  return (
    <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <CalendarIcon className="w-6 h-6 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Expected Payments Calendar</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={prevMonth}
            className="p-1 hover:bg-gray-100 rounded-lg"
          >
            ←
          </button>
          <span className="font-medium">
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </span>
          <button
            onClick={nextMonth}
            className="p-1 hover:bg-gray-100 rounded-lg"
          >
            →
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-blue-50 rounded-lg p-3">
          <p className="text-xs text-blue-600">Total Expected</p>
          <p className="text-lg font-bold text-blue-900">
            KSh {stats.totalExpected?.toLocaleString()}
          </p>
        </div>
        <div className="bg-purple-50 rounded-lg p-3">
          <p className="text-xs text-purple-600">Invoices</p>
          <p className="text-lg font-bold text-purple-900">{stats.totalInvoices}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-3">
          <p className="text-xs text-green-600">Avg/Day</p>
          <p className="text-lg font-bold text-green-900">
            KSh {Math.round(stats.totalExpected / (daysInMonth || 1)).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Scrollable Date Strip — only days with data */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-500">
            {dataDays.length} date{dataDays.length !== 1 ? 's' : ''} with expected payments
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={goToPrevDate}
              disabled={!selectedDate || dataDays.findIndex((d: any) => d.date === selectedDate) <= 0}
              className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-40"
            >
              Prev Date
            </button>
            <button
              onClick={goToNextDate}
              disabled={!!selectedDate && dataDays.findIndex((d: any) => d.date === selectedDate) >= dataDays.length - 1}
              className="px-2 py-1 text-xs bg-primary-100 text-primary-700 hover:bg-primary-200 rounded disabled:opacity-40"
            >
              Next Date
            </button>
          </div>
        </div>
        {dataDays.length > 0 ? (
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin">
            {dataDays.map((day: any) => (
              <button
                key={day.date}
                onClick={() => setSelectedDate(selectedDate === day.date ? null : day.date)}
                className={`flex-shrink-0 w-16 h-20 flex flex-col items-center justify-center rounded-lg text-sm transition-colors border ${
                  selectedDate === day.date
                    ? 'bg-primary-100 border-primary-500 ring-2 ring-primary-200'
                    : 'bg-green-50 hover:bg-green-100 border-green-200'
                }`}
              >
                <span className="text-xs text-gray-500 uppercase">
                  {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                </span>
                <span className="text-lg font-bold text-green-800">
                  {day.day}
                </span>
                <span className="text-xs text-green-600 font-semibold">
                  {day.data.invoiceCount} inv
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400 text-sm">
            No expected payments this month
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(stats.byCategory || {}).map(([code, data]: [string, any]) => (
          <span key={code} className={`text-xs px-2 py-1 rounded-full border ${getCategoryColorClass(code)}`}>
            {data.name}: KSh {data.amount?.toLocaleString()}
          </span>
        ))}
      </div>

      {/* Selected Date Details */}
      {selectedDayData && (
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">
              {new Date(selectedDate!).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
            </h3>
            <span className="text-lg font-bold text-primary-600">
              KSh {selectedDayData.totalAmount?.toLocaleString()}
            </span>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {selectedDayData.items?.map((item: any) => (
              <Link
                key={item.invoiceId}
                to={`/invoices/${item.invoiceId}`}
                className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-900">{item.customerName}</p>
                    <p className="text-sm text-gray-500">{item.invoiceNumber}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryColorClass(item.category)}`}>
                      {item.categoryName}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">
                      KSh {item.amount?.toLocaleString()}
                    </p>
                    {item.paymentTermsDays > 0 && (
                      <p className="text-xs text-gray-500">Net {item.paymentTermsDays}</p>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Quick Stats Widget
const QuickStatsWidget = () => {
  const { data: statsData, isLoading } = useQuery({
    queryKey: ['dashboard', 'customer-types-summary'],
    queryFn: () => dashboardService.getCustomerTypesSummary(),
  })

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      </div>
    )
  }

  const data = statsData?.data?.data
  if (!data) return null

  const { quickStats, byGroup, consignment } = data

  return (
    <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
      <div className="flex items-center gap-3 mb-4">
        <ChartBarIcon className="w-6 h-6 text-primary-600" />
        <h2 className="text-lg font-semibold text-gray-900">Customer Balances Overview</h2>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
          <p className="text-sm text-blue-600">Total Outstanding</p>
          <p className="text-2xl font-bold text-blue-900">
            KSh {quickStats.totalOutstanding?.toLocaleString()}
          </p>
          <p className="text-xs text-blue-500 mt-1">
            {quickStats.totalCustomers} customers
          </p>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4">
          <p className="text-sm text-amber-600">Due This Week</p>
          <p className="text-2xl font-bold text-amber-900">
            KSh {quickStats.totalDueThisWeek?.toLocaleString()}
          </p>
          <p className="text-xs text-amber-500 mt-1">Expected payments</p>
        </div>
      </div>

      {/* Customer Type Cards */}
      <div className="space-y-3">
        {byGroup?.map((group: any) => (
          <div key={group.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${
                group.code === 'corporate' ? 'bg-amber-500' :
                group.code === 'wholesale' ? 'bg-blue-500' :
                group.code === 'consignment' ? 'bg-purple-500' :
                'bg-green-500'
              }`} />
              <div>
                <p className="font-medium text-gray-900">{group.name}</p>
                <p className="text-xs text-gray-500">
                  {group.customerCount} clients • Net {group.paymentTermsDays}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-semibold text-gray-900">
                KSh {group.totalBalance?.toLocaleString()}
              </p>
              {group.totalInvoices > 0 && (
                <p className="text-xs text-gray-500">{group.totalInvoices} invoices</p>
              )}
            </div>
          </div>
        ))}
        
        {/* Consignment Summary */}
        {consignment?.customerCount > 0 && (
          <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-100">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-purple-500" />
              <div>
                <p className="font-medium text-purple-900">Consignment Clients</p>
                <p className="text-xs text-purple-600">
                  {consignment.customerCount} active • {consignment.activeConsignments} consignments
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-semibold text-purple-900">
                KSh {consignment.totalBalance?.toLocaleString()}
              </p>
              <p className="text-xs text-purple-600">
                KSh {consignment.totalConsignmentValue?.toLocaleString()} on consignment
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export const DashboardPage = () => {
  const [selectedCategory, setSelectedCategory] = useState<any>(null)
  const [selectedPaymentMode, setSelectedPaymentMode] = useState<any>(null)

  const { data: dashboardData, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => dashboardService.getSummary(),
  })

  const { data: salesChartData } = useQuery({
    queryKey: ['dashboard', 'sales-chart'],
    queryFn: () => dashboardService.getSalesChart(),
  })

  const { data: customerCategoriesData } = useQuery({
    queryKey: ['dashboard', 'customer-categories'],
    queryFn: () => dashboardService.getCustomerCategories(),
  })

  const { data: paymentModesData } = useQuery({
    queryKey: ['dashboard', 'payment-modes'],
    queryFn: () => dashboardService.getPaymentModes(),
  })

  const { data: smsCreditsData } = useQuery({
    queryKey: ['sms-credits'],
    queryFn: () => notificationService.getSMSCredits(),
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  const stats = dashboardData?.data?.data?.stats || {}
  const alerts = dashboardData?.data?.data?.alerts || []
  const recentActivity = dashboardData?.data?.data?.recentActivity || []
  const customerCategories = customerCategoriesData?.data?.data || []
  const paymentModes = paymentModesData?.data?.data || []
  const smsCredits = smsCreditsData?.data?.data

  // Filter out categories with zero balance for cleaner UI, but keep if they have customers
  const activeCategories = customerCategories.filter(
    (cat: any) => cat.totalBalance > 0 || cat.customerCount > 0
  )

  // Filter out payment modes with zero transactions this month
  const activePaymentModes = paymentModes.filter(
    (mode: any) => mode.monthAmount > 0 || mode.todayAmount > 0
  )

  // SMS Credits Card Component
  const SMSCreditsCard = () => {
    const credits = smsCredits?.remaining_credits || 0
    const total = smsCredits?.total_credits || 0
    const percentage = total > 0 ? (credits / total) * 100 : 0
    const isLow = credits < (smsCredits?.alert_threshold || 100)

    return (
      <Link to="/notifications" className="block">
        <div className={`bg-white rounded-xl shadow border p-6 transition-shadow hover:shadow-lg ${isLow ? 'border-red-200' : 'border-gray-100'}`}>
          <div className="flex items-center">
            <div className={`flex-shrink-0 p-3 rounded-lg ${isLow ? 'bg-red-50' : 'bg-blue-50'}`}>
              <DevicePhoneMobileIcon className={`w-6 h-6 ${isLow ? 'text-red-600' : 'text-blue-600'}`} />
            </div>
            <div className="ml-4 flex-1">
              <p className="text-sm font-medium text-gray-500">SMS Credits</p>
              <p className={`text-2xl font-bold ${isLow ? 'text-red-600' : 'text-gray-900'}`}>
                {credits.toLocaleString()}
              </p>
            </div>
            {isLow && (
              <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                Low
              </span>
            )}
          </div>
          <div className="mt-3">
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full ${isLow ? 'bg-red-500' : 'bg-blue-500'}`}
                style={{ width: `${Math.max(percentage, 5)}%` }}
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {smsCredits?.used_credits?.toLocaleString() || 0} used of {total.toLocaleString()}
            </p>
          </div>
        </div>
      </Link>
    )
  }

  return (
    <div className="space-y-6">
      {/* Modals */}
      <CustomerCategoryModal
        category={selectedCategory}
        isOpen={!!selectedCategory}
        onClose={() => setSelectedCategory(null)}
      />
      <PaymentModeModal
        mode={selectedPaymentMode}
        isOpen={!!selectedPaymentMode}
        onClose={() => setSelectedPaymentMode(null)}
      />

      {/* Page header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">
            {new Date().toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </span>
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {alerts.map((alert: any) => (
            <AlertCard
              key={alert.id}
              title={alert.title}
              count={alert.count}
              type={alert.type}
              href={alert.href}
            />
          ))}
        </div>
      )}

      {/* Customer Categories Section */}
      {activeCategories.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Customer Categories</h2>
            <Link
              to="/customers"
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
            >
              View All <ChevronRightIcon className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {activeCategories.map((category: any) => (
              <StatCard
                key={category.id}
                title={category.name}
                value={`KSh ${category.totalBalance?.toLocaleString()}`}
                subtitle={`${category.customerCount} customers • ${category.totalInvoices} open invoices`}
                icon={BuildingOfficeIcon}
                color={getCategoryColor(category.code)}
                onClick={() => setSelectedCategory(category)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Payment Modes Section */}
      {activePaymentModes.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Payment Modes</h2>
            <Link
              to="/payments"
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
            >
              View All <ChevronRightIcon className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {activePaymentModes.map((mode: any) => (
              <StatCard
                key={mode.id}
                title={mode.name}
                value={`KSh ${mode.monthAmount?.toLocaleString()}`}
                subtitle={`${mode.monthCount} this month • ${mode.todayCount} today`}
                icon={getPaymentIcon(mode.code)}
                color="green"
                onClick={() => setSelectedPaymentMode(mode)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Payment Alerts Widget */}
      <PaymentAlertsWidget />

      {/* Quick Stats & Calendar Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <QuickStatsWidget />
        <PaymentCalendarWidget />
      </div>

      {/* Main Stats Grid */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatCard
            title="Today's Sales"
            value={stats.todaySales || 'KSh 0'}
            change="+12.5%"
            changeType="positive"
            icon={CurrencyDollarIcon}
            href="/sales"
            color="primary"
            detail={{
              label: 'Profit',
              value: stats.todayProfit || 'KSh 0',
              percent: (() => {
                const sales = parseKsh(stats.todaySales)
                const profit = parseKsh(stats.todayProfit)
                return sales > 0 ? (profit / sales) * 100 : 0
              })(),
            }}
          />
          <StatCard
            title="This Month"
            value={stats.monthSales || 'KSh 0'}
            change="+8.2%"
            changeType="positive"
            icon={ShoppingBagIcon}
            href="/sales"
            color="blue"
            detail={{
              label: 'Profit',
              value: stats.monthProfit || 'KSh 0',
              percent: (() => {
                const sales = parseKsh(stats.monthSales)
                const profit = parseKsh(stats.monthProfit)
                return sales > 0 ? (profit / sales) * 100 : 0
              })(),
            }}
          />
          <StatCard
            title="Products"
            value={stats.totalProducts?.toString() || '0'}
            icon={CubeIcon}
            href="/products"
            color="amber"
          />
          <StatCard
            title="Customers"
            value={stats.totalCustomers?.toString() || '0'}
            icon={UsersIcon}
            href="/customers"
            color="purple"
          />
          {/* SMS Credits Card */}
          <SMSCreditsCard />
          {/* Notifications Quick Link */}
          <Link to="/notifications" className="block">
            <div className="bg-white rounded-xl shadow border border-gray-100 p-6 transition-shadow hover:shadow-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0 p-3 bg-purple-50 rounded-lg">
                  <ChatBubbleLeftRightIcon className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-gray-500">Notifications</p>
                  <p className="text-lg font-bold text-gray-900">Send Messages</p>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Email, SMS & Bulk messaging
              </p>
            </div>
          </Link>
        </div>
      </div>

      {/* Charts and Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Sales Overview
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={salesChartData?.data?.data || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="sales"
                  stroke="#0d9488"
                  strokeWidth={2}
                  dot={{ fill: '#0d9488' }}
                />
                <Line
                  type="monotone"
                  dataKey="profit"
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={{ fill: '#22c55e' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Recent Activity
          </h3>
          <div className="space-y-4">
            {recentActivity.length === 0 ? (
              <p className="text-gray-500 text-sm">No recent activity</p>
            ) : (
              recentActivity.map((activity: any) => (
                <div
                  key={activity.id}
                  className="flex items-start gap-3 pb-4 border-b border-gray-100 last:border-0"
                >
                  <div
                    className={`w-2 h-2 rounded-full mt-2 ${
                      activity.type === 'sale'
                        ? 'bg-green-500'
                        : activity.type === 'purchase'
                        ? 'bg-blue-500'
                        : 'bg-yellow-500'
                    }`}
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {activity.description}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {activity.time}
                    </p>
                  </div>
                  {activity.amount && (
                    <span className="text-sm font-medium text-gray-900">
                      {activity.amount}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link
            to="/pos"
            className="flex flex-col items-center justify-center p-6 bg-white rounded-xl shadow border border-gray-100 hover:shadow-lg transition-shadow"
          >
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-3">
              <ShoppingBagIcon className="w-6 h-6 text-primary-600" />
            </div>
            <span className="font-medium text-gray-900">New Sale</span>
          </Link>
          <Link
            to="/purchases/new"
            className="flex flex-col items-center justify-center p-6 bg-white rounded-xl shadow border border-gray-100 hover:shadow-lg transition-shadow"
          >
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
              <CubeIcon className="w-6 h-6 text-green-600" />
            </div>
            <span className="font-medium text-gray-900">Add Purchase</span>
          </Link>
          <Link
            to="/products/new"
            className="flex flex-col items-center justify-center p-6 bg-white rounded-xl shadow border border-gray-100 hover:shadow-lg transition-shadow"
          >
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mb-3">
              <CubeIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <span className="font-medium text-gray-900">Add Product</span>
          </Link>
          <Link
            to="/consignments/new"
            className="flex flex-col items-center justify-center p-6 bg-white rounded-xl shadow border border-gray-100 hover:shadow-lg transition-shadow"
          >
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-3">
              <TruckIcon className="w-6 h-6 text-purple-600" />
            </div>
            <span className="font-medium text-gray-900">New Consignment</span>
          </Link>
        </div>
      </div>
    </div>
  )
}
